import "./Text.scss";

import React, {
	ReactElement,
	ReactNode,
	ReactNodeArray,
	useContext,
	useMemo,
} from "react";
import clsx from "clsx";

import { cleanHTMLString } from "../../../lib/util/StringUtils";

import {
	Locale,
	LocaleContext,
	TextElement,
	translate,
} from "../../../lib/util/LocaleUtils";
import { ElementType, ExtractMixinProps } from "../../../lib/types/UtilTypes";
import {
	computeLayoutClasses,
	LayoutProps,
} from "../../../lib/util/ReactUtils";
import { Translatable } from "../../../lib/util/Translatable";

export interface BaseTextProps<C extends ElementType = "span">
	extends LayoutProps {
	tag?: C;
	safeHtml?: boolean;
	inline?: boolean;
	map?: (value: string) => string;
	className?: string;
	lineThrough?: boolean;
	children?: React.ReactNode | TextElement | boolean;
	forwardRef?: React.MutableRefObject<HTMLElement | null>;
}

export type TextProps<C extends ElementType> = ExtractMixinProps<
	BaseTextProps<C>
>;

const pushContent = (content: any[], element: any) => {
	content.push(
		React.isValidElement(element)
			? React.cloneElement(element, { key: content.length })
			: element,
	);
};

const processText = (
	text: TextElement | undefined,
	locale: Locale | undefined,
	map: Function,
	safeHtml?: boolean,
): ReactNode | ReactNode[] => {
	if (text === undefined) return text;

	let string: ReactNode | ReactNode[] = translate(text, locale);
	if (map) {
		string = map(string);
	}

	if (safeHtml && typeof string === "string") {
		const sanitizedHTML = cleanHTMLString(string, {
			ALLOWED_ATTR: ["target", "href", "rel", "class"],
		});
		return <span dangerouslySetInnerHTML={{ __html: sanitizedHTML }} />;
	}
	return string;
};

function Text<C extends ElementType = "span">(
	props: TextProps<C>,
): ReactElement {
	const {
		children,
		tag,
		safeHtml,
		className,
		inline,
		map,
		lineThrough,
		forwardRef,
		subtle,
		...otherProps
	} = props;
	const [layoutClasses, remainingProps] = computeLayoutClasses(otherProps);

	const text = children;

	let element: ElementType = "span";
	if (tag) {
		element = tag;
	}

	let textProps = {
		ref: forwardRef,
		className: clsx(
			"Text",
			layoutClasses,
			className,
			tag && `--${React.Fragment === tag ? "fragment" : tag}`,
			lineThrough && "--lineThrough",
		),
		...remainingProps,
	};

	if (text instanceof Translatable && text.props) {
		textProps = {
			...textProps,
			...text.props,
			className: clsx(textProps.className, text.props.className),
		};
	}

	if (inline || element === React.Fragment) {
		element = React.Fragment;
		delete (textProps as any).className;
	}

	const { locale } = useContext(LocaleContext) || {};

	const content: ReactNode = useMemo(() => {
		if (Array.isArray(text)) {
			const textArray = text as ReactNodeArray;
			const content: ReactNode[] = textArray.reduce((content, text) => {
				const string = processText(text as any, locale, map as any, safeHtml);
				if (Array.isArray(string)) {
					string.forEach((element) => pushContent(content as any, element));
				} else {
					pushContent(content as any, string);
				}
				return content;
			}, []) as any;
			return content.length === 1 ? content[0] : content;
		}
		return processText(text as any, locale, map as any, safeHtml);
	}, [text, locale, safeHtml, map]);

	return React.createElement(element, textProps, content);
}

export default React.memo(Text);
